import { showErrorToast } from '@bpinternal/ui-kit-next'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useAsync } from 'react-use'
import { launchUserSegmentationSurvey } from '../features/marketing/'
import { DEFAULT_WORKSPACE_NAME } from '../features/workspaces/types'
import { useCreateWorkspace, useCurrentUser, useFeature, useWorkspaces } from '../hooks'
import { userPreferencesQueryOptions } from '../queries/user'
import { getQueryKey } from '../services'
import { trackEvent } from './SegmentProvider'

type Props = {
  children?: React.ReactNode
}

// Only users created after 31st Oct, 2025 are eligible to see the user segmentation survey
const ONBOARDING_SURVEY_START_DATE = new Date('2025-10-31T00:00:00Z')

const isUserEligibleForOnboardingSurvey = (userCreatedAt: string) => {
  // If we don't have a creation date, assume they are eligible
  if (!userCreatedAt) return true

  const createdAt = new Date(userCreatedAt)
  return createdAt >= ONBOARDING_SURVEY_START_DATE
}

export const WorkspaceProvider = ({ children }: Props) => {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const { data: workspaces } = useWorkspaces()
  const { mutateAsync: createWorkspace } = useCreateWorkspace()
  const canShowUserSegmentationSurvey = useFeature('dashboard-post-signup-fork')
  const { data: requireOnboarding } = useSuspenseQuery(userPreferencesQueryOptions({ path: 'requireOnboarding' }))

  async function ensureDefaultWorkspace() {
    if (workspaces && workspaces.length === 0) {
      await createWorkspace({ name: DEFAULT_WORKSPACE_NAME })
      await queryClient.invalidateQueries({ queryKey: getQueryKey('workspaces_') })
    }
  }

  useAsync(async () => {
    if (workspaces) {
      await ensureDefaultWorkspace().catch((e) => {
        showErrorToast(
          "Sorry, we couldn't automatically create a workspace for you. Please try creating your first workspace manually."
        )
        // eslint-disable-next-line no-console
        console.error(e)
      })
    }
  }, [user, workspaces])

  useAsync(async () => {
    // Only proceed if we have workspaces AND onboarding is required
    // This prevents race condition where user segmentation survey launches before workspace is created
    if (!workspaces?.length) {
      return
    }

    if (!(requireOnboarding && isUserEligibleForOnboardingSurvey(user.createdAt))) {
      return
    }

    const workspaceId = workspaces[0]?.id as string

    if (canShowUserSegmentationSurvey) {
      trackEvent({ type: 'post_signup_fork_presented' })
      await launchUserSegmentationSurvey(workspaceId)
    }
  }, [workspaces, requireOnboarding, canShowUserSegmentationSurvey])

  if (!workspaces?.length) {
    return null
  }

  return <>{children}</>
}
